import { Box, BoxProps } from '@chakra-ui/react';
import { FeatureCollection, Point } from 'geojson';
import mapbox, { CircleLayerSpecification } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useMemo, useRef } from 'react';

interface Props extends BoxProps {
  lat: number;
  long: number;
}

const MAP_STYLE = 'mapbox://styles/dammmmien/cm5mjyhre00ap01s9denmak3h';
const DEFAULT_ZOOM = 13;
const DEFAULT_MIN_ZOOM = 10.5;
const DEFAULT_MAX_ZOOM = 15;

const pointLayer: CircleLayerSpecification = {
  id: 'point',
  type: 'circle',
  source: 'point',
  paint: {
    'circle-radius': [
      'case',
      ['==', ['get', 'type'], 'inner'],
      4, // Inner solid circle radius
      50 // Outer circle radius
    ],
    'circle-color': '#000000',
    'circle-opacity': [
      'case',
      ['==', ['get', 'type'], 'inner'],
      1, // Inner circle: 100% opacity
      0.2 // Outer circle: 20% opacity
    ],
    'circle-stroke-width': [
      'case',
      ['==', ['get', 'type'], 'inner'],
      0, // Inner circle: no stroke
      1 // Outer circle: 1px stroke
    ],
    'circle-stroke-color': '#000000',
    'circle-stroke-opacity': 0.4 // Border stroke: 40% opacity
  }
};

mapbox.accessToken = process.env.MAPBOX_ACCESS_TOKEN;

export function InteractiveMap({ lat, long, ...boxProps }: Props) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map>(undefined);

  const geojson = useMemo(() => {
    const point: FeatureCollection<Point> = {
      type: 'FeatureCollection',
      features: [
        // Outer circle with border
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [long, lat]
          },
          properties: { type: 'outer' }
        },
        // Inner solid circle
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [long, lat]
          },
          properties: { type: 'inner' }
        }
      ]
    };
    return point;
  }, [lat, long]);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    mapRef.current = new mapbox.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      center: { lng: long, lat },
      zoom: DEFAULT_ZOOM,
      minZoom: DEFAULT_MIN_ZOOM,
      maxZoom: DEFAULT_MAX_ZOOM,
      fadeDuration: 0,
      scrollZoom: false
    });

    mapRef.current.addControl(
      new mapbox.NavigationControl({ showCompass: false })
    );

    mapRef.current.on('load', () => {
      if (!mapRef.current) {
        return;
      }

      mapRef.current.addSource('point', {
        type: 'geojson',
        data: geojson
      });

      mapRef.current.addLayer(pointLayer);
    });

    return () => mapRef.current?.remove();
  }, []);

  return (
    <Box
      ref={mapContainerRef}
      width="100%"
      height="100%"
      minH="400px"
      borderRadius="md"
      overflow="hidden"
      {...boxProps}
    />
  );
}